import { Col, Typography } from "antd";
import Image from "next/image";
import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import { getTrainClassTypeBaseOnProduct } from "../../Lib/Utils/validationUtil";
import { useRecState } from "../../Orchestrator/Recoils/Events/Recoil";
import { routeTo } from "../CustomHooks/routeChange";
import {
  ProductTypeEnum,
  TrainTypeEnum,
  TripTypeEnum,
} from "../../Orchestrator/Types/Enums/globalDefaultEnum";
import { IBookingResponse } from "../../Orchestrator/Types/Index";
import { IHelpInfoSectionProps } from "../../Orchestrator/Types/Interface/IServiceRequest";

// "Helpful information" steps box shown again per business request (2026-06-17).
const SHOW_HELP_INFO_STEPS = true;

const HelpInfoSection: React.FC<IHelpInfoSectionProps> = ({
  infoType = "",
}) => {
  const intl = useIntl();
  const [booking] = useRecState<IBookingResponse>("bookingResponseAtom");
  const [selectedTripForServiceRequest] = useRecState<string[]>(
    "selectedTripForServiceRequestAtom"
  );
  const [isAuthenticated] = useRecState<boolean>("isAuthenticatedStateAtom");

  // The underlined keywords in the Helpful information steps are navigable
  // links: top-level destinations go straight to their page; booking-action
  // steps (cancel/change/upgrade/etc.) go to the bookings list since the target
  // action requires the user to first open a specific booking.
  const getLinkProps = (path: string) => ({
    className: "cursor-pointer fs-14 fw-400",
    role: "link" as const,
    tabIndex: 0,
    onClick: () => routeTo(path),
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        routeTo(path);
      }
    },
  });
  const homeSearchPath = isAuthenticated ? "/search" : "/";
  const renderBookingProduct = useMemo(() => {
    const productData = {
      tripClass: "",
      description: "",
    };
    if (
      booking?.booking?.id &&
      infoType === "COMPENSATION_AND_REFUND_CLASS_EXP"
    ) {
      for (const offer of booking.booking.bookedOffers) {
        for (const admission of offer.admissions) {
          if (admission.status === "FULFILLED") {
            const tripDirection = booking.booking.trips.find(
              (d) => d.id === offer.tripCoverage.coveredTripId
            )?.direction;
            let direction =
              tripDirection === TripTypeEnum.OUT_BOUND
                ? TrainTypeEnum.outbound
                : TrainTypeEnum.inbound;
            if (selectedTripForServiceRequest.includes(direction)) {
              const product = offer.products.find(
                (pro) =>
                  pro.type === ProductTypeEnum.ADMISSION_POINT2POINT &&
                  pro.id === admission.products[0].productId
              );

              if (product && productData.tripClass === "") {
                const tripClass = getTrainClassTypeBaseOnProduct([product]);
                if (tripClass != null) {
                  productData.tripClass = tripClass;
                  productData.description =
                    product.descriptiveTexts[0]?.description || "";
                  break;
                }
              }
            }
          }

          if (productData.tripClass) {
            break;
          }
        }
      }
    }
    if (!productData.tripClass) return "";
    return (
      <div className="ticket-info-standard">
        <strong
          className={
            productData.tripClass === "premium"
              ? "premium-color"
              : "standard-color"
          }
        >
          {intl.formatMessage({ id: productData.tripClass })}
        </strong>
        <div>
          <span className="color-757575 fw-500">{productData.description}</span>
        </div>
      </div>
    );
  }, [booking?.booking?.id, selectedTripForServiceRequest]);

  if (infoType === "REQUEST_INVOICE") {
    return (
      <Col span={24} className="filed-help-info">
        <div>
          <div className={SHOW_HELP_INFO_STEPS ? "border-light-grey ff-inter" : "d-none"}>
            <div className="p-20 d-flex info-section align-items-center">
              <Image
                src="/assets/fmlm/info-circle.svg"
                alt=""
                height={25}
                width={25}
                className="mr-10"
              />
              <span className="fs-14 fw-500 ff-inter">
                {intl.formatMessage({ id: "HELP_INFO" })}
              </span>
            </div>
            <div className="p-20 ff-inter">
              <div className="mb-10 fs-14 fw-400 ff-inter color-757575">
                {intl.formatMessage({ id: "CHANGE_BOOKING" })}:
              </div>
              <div className="p-5">
                <ul>
                  <li className="mb-5 fs-14 fw-400 color-757575">
                    1. {intl.formatMessage({ id: "CLICK_BOOKING" })}
                  </li>
                  <li className="mb-5 fs-14 fw-400 color-757575">
                    2. {intl.formatMessage({ id: "DESIRED_BOOKING" })}
                  </li>
                  <li className="mb-5 fs-14 fw-400 color-757575">
                    3. {intl.formatMessage({ id: "MANAGE_BOOKING" })}
                  </li>
                  <li className="mb-5">
                    <span className="mr-5 color-757575 fs-14 fw-400">
                      4. {intl.formatMessage({ id: "select" })}
                    </span>
                    <u {...getLinkProps("/bookings")}>
                      {intl.formatMessage({ id: "REQUEST_INVOICE_TEXT" })}
                    </u>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="mt-10 mb-10">
            <Typography.Text className="fw-700 fs-18 ff-inter">
              {intl.formatMessage({ id: "SUBMIT_REQUEST" })}
            </Typography.Text>
          </div>
        </div>
      </Col>
    );
  } else if (infoType === "OTHERS") {
    return (
      <Col span={24} className="filed-help-info">
        <div>
          <div className={SHOW_HELP_INFO_STEPS ? "border-light-grey ff-inter" : "d-none"}>
            <div className="p-20 d-flex info-section align-items-center">
              <Image
                src="/assets/fmlm/info-circle.svg"
                alt=""
                height={25}
                width={25}
                className="mr-10"
              />
              <span className="fs-14 fw-500 ff-inter">
                {intl.formatMessage({ id: "HELP_INFO" })}
              </span>
            </div>
            <div className="p-20 ff-inter">
              <div className="mb-10 fs-14 fw-400 ff-inter color-757575">
                {intl.formatMessage({ id: "CHANGE_BOOKING" })}:
              </div>
              <div className="p-5">
                <ul>
                  <li className="mb-5 fs-14 fw-400 color-757575">
                    1. {intl.formatMessage({ id: "CLICK_MENU" })}
                  </li>
                  <li className="mb-5">
                    <span className="mr-5 color-757575 fs-14 fw-400">
                      2. {intl.formatMessage({ id: "select" })}
                    </span>
                    <u {...getLinkProps("/help")}>
                      {intl.formatMessage({ id: "contact_us" })}
                    </u>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="mt-10 mb-10">
            <Typography.Text className="fw-700 fs-18 ff-inter">
              {intl.formatMessage({ id: "SUBMIT_REQUEST" })}
            </Typography.Text>
          </div>
        </div>
      </Col>
    );
  } else if (infoType === "NEED_HELP") {
    return (
      <Col span={24} className="filed-help-info">
        <div>
          <div className={SHOW_HELP_INFO_STEPS ? "border-light-grey ff-inter" : "d-none"}>
            <div className="p-20 d-flex info-section align-items-center">
              <Image
                src="/assets/fmlm/info-circle.svg"
                alt=""
                height={25}
                width={25}
                className="mr-10"
              />
              <span className="fs-14 fw-500 ff-inter">
                {intl.formatMessage({ id: "HELP_INFO" })}
              </span>
            </div>
            <div className="p-20 ff-inter">
              <div className="mb-10 fs-14 fw-400 ff-inter color-757575">
                {intl.formatMessage({ id: "CHANGE_BOOKING" })}:
              </div>
              <div className="p-5">
                <ul>
                  <li className="mb-5 fs-14 fw-400 color-757575">
                    1. {intl.formatMessage({ id: "CLICK_MENU" })}
                  </li>
                  <li className="mb-5">
                    <span className="mr-5 color-757575 fs-14 fw-400">
                      2. {intl.formatMessage({ id: "select" })}
                    </span>
                    <u {...getLinkProps("/help")}>
                      {intl.formatMessage({ id: "contact_us" })}
                    </u>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="mt-10 mb-10">
            <Typography.Text className="fw-700 fs-18 ff-inter">
              {intl.formatMessage({ id: "SUBMIT_REQUEST" })}
            </Typography.Text>
          </div>
        </div>
      </Col>
    );
  } else if (infoType === "MY_ACCOUNT") {
    return (
      <Col span={24} className="filed-help-info">
        <div>
          <div className={SHOW_HELP_INFO_STEPS ? "border-light-grey ff-inter" : "d-none"}>
            <div className="p-20 d-flex info-section align-items-center">
              <Image
                src="/assets/fmlm/info-circle.svg"
                alt=""
                height={25}
                width={25}
                className="mr-10"
              />
              <span className="fs-14 fw-500 ff-inter">
                {intl.formatMessage({ id: "HELP_INFO" })}
              </span>
            </div>
            <div className="p-20 ff-inter">
              <div className="mb-10 fs-14 fw-400 ff-inter color-757575">
                {intl.formatMessage({ id: "CHANGE_BOOKING" })}:
              </div>
              <div className="p-5">
                <ul>
                  <li className="mb-5 fs-14 fw-400 color-757575">
                    1. {intl.formatMessage({ id: "CLICK_MENU" })}
                  </li>
                  <li className="mb-5">
                    <span className="mr-5 color-757575 fs-14 fw-400">
                      2. {intl.formatMessage({ id: "select" })}
                    </span>
                    <u {...getLinkProps("/my-account")}>
                      {intl.formatMessage({ id: "myAccount" })}
                    </u>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="mt-10 mb-10">
            <Typography.Text className="fw-700 fs-18 ff-inter">
              {intl.formatMessage({ id: "SUBMIT_REQUEST" })}
            </Typography.Text>
          </div>
        </div>
      </Col>
    );
  } else if (infoType === "COMPENSATION_AND_REFUND_CLASS_EXP") {
    return (
      <Col span={24} className="filed-help-info">
        <div>
          <div className={SHOW_HELP_INFO_STEPS ? "border-light-grey ff-inter" : "d-none"}>
            <div className="p-20 d-flex info-section align-items-center">
              <Image
                src="/assets/fmlm/info-circle.svg"
                alt=""
                height={25}
                width={25}
                className="mr-10"
              />
              <span className="fs-14 fw-500 ff-inter">
                {intl.formatMessage({ id: "HELP_INFO" })}
              </span>
            </div>
            <div className="p-20 ff-inter">
              <div className="mb-10 fs-14 fw-400 ff-inter color-757575">
                {intl.formatMessage({ id: "CompensationRefundText" })}:
              </div>
              <div className="p-5">{renderBookingProduct}</div>
            </div>
          </div>
          <div className="mt-10 mb-10">
            <Typography.Text className="fw-700 fs-18 ff-inter">
              {intl.formatMessage({ id: "SUBMIT_REQUEST" })}
            </Typography.Text>
          </div>
        </div>
      </Col>
    );
  } else if (infoType === "JOURNEY_PLANNER") {
    return (
      <Col span={24} className="filed-help-info">
        <div>
          <div className={SHOW_HELP_INFO_STEPS ? "border-light-grey ff-inter" : "d-none"}>
            <div className="p-20 d-flex info-section align-items-center">
              <Image
                src="/assets/fmlm/info-circle.svg"
                alt=""
                height={25}
                width={25}
                className="mr-10"
              />
              <span className="fs-14 fw-500 ff-inter">
                {intl.formatMessage({ id: "HELP_INFO" })}
              </span>
            </div>
            <div className="p-20 ff-inter">
              <div className="mb-10 fs-14 fw-400 ff-inter color-757575">
                {intl.formatMessage({ id: "CHANGE_BOOKING" })}:
              </div>
              <div className="p-5">
                <ul>
                  <li className="mb-5 fs-14 fw-400 color-757575">
                    1. {intl.formatMessage({ id: "CLICK_MENU" })}
                  </li>
                  <li className="mb-5">
                    <span className="mr-5 color-757575 fs-14 fw-400">
                      2. {intl.formatMessage({ id: "select" })}
                    </span>
                    <u {...getLinkProps("/journey-planner")}>
                      {intl.formatMessage({ id: "journeyPlanner" })}
                    </u>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="mt-10 mb-10">
            <Typography.Text className="fw-700 fs-18 ff-inter">
              {intl.formatMessage({ id: "SUBMIT_REQUEST" })}
            </Typography.Text>
          </div>
        </div>
      </Col>
    );
  } else if (infoType === "TRAIN_SCHEDULE") {
    return (
      <Col span={24} className="filed-help-info">
        <div>
          <div className={SHOW_HELP_INFO_STEPS ? "border-light-grey ff-inter" : "d-none"}>
            <div className="p-20 d-flex info-section align-items-center">
              <Image
                src="/assets/fmlm/info-circle.svg"
                alt=""
                height={25}
                width={25}
                className="mr-10"
              />
              <span className="fs-14 fw-500 ff-inter">
                {intl.formatMessage({ id: "HELP_INFO" })}
              </span>
            </div>
            <div className="p-20 ff-inter">
              <div className="mb-10 fs-14 fw-400 ff-inter color-757575">
                {intl.formatMessage({ id: "CHANGE_BOOKING" })}:
              </div>
              <div className="p-5">
                <ul>
                  <li className="mb-5 fs-14 fw-400 color-757575">
                    1. {intl.formatMessage({ id: "CLICK_MENU" })}
                  </li>
                  <li className="mb-5">
                    <span className="mr-5 color-757575 fs-14 fw-400">
                      2. {intl.formatMessage({ id: "select" })}
                    </span>
                    <u {...getLinkProps("/live-schedule")}>
                      {intl.formatMessage({ id: "LiveTrainSchedule" })}
                    </u>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="mt-10 mb-10">
            <Typography.Text className="fw-700 fs-18 ff-inter">
              {intl.formatMessage({ id: "SUBMIT_REQUEST" })}
            </Typography.Text>
          </div>
        </div>
      </Col>
    );
  } else if (infoType === "STATIONS") {
    return (
      <Col span={24} className="filed-help-info">
        <div>
          <div className={SHOW_HELP_INFO_STEPS ? "border-light-grey ff-inter" : "d-none"}>
            <div className="p-20 d-flex info-section align-items-center">
              <Image
                src="/assets/fmlm/info-circle.svg"
                alt=""
                height={25}
                width={25}
                className="mr-10"
              />
              <span className="fs-14 fw-500 ff-inter">
                {intl.formatMessage({ id: "HELP_INFO" })}
              </span>
            </div>
            <div className="p-20 ff-inter">
              <div className="mb-10 fs-14 fw-400 ff-inter color-757575">
                {intl.formatMessage({ id: "CHANGE_BOOKING" })}:
              </div>
              <div className="p-5">
                <ul>
                  <li className="mb-5 fs-14 fw-400 color-757575">
                    1. {intl.formatMessage({ id: "CLICK_MENU" })}
                  </li>
                  <li className="mb-5">
                    <span className="mr-5 color-757575 fs-14 fw-400">
                      2. {intl.formatMessage({ id: "select" })}
                    </span>
                    <u {...getLinkProps("/networks-maps-stations")}>
                      {intl.formatMessage({ id: "stationsTitle" })}
                    </u>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="mt-10 mb-10">
            <Typography.Text className="fw-700 fs-18 ff-inter">
              {intl.formatMessage({ id: "SUBMIT_REQUEST" })}
            </Typography.Text>
          </div>
        </div>
      </Col>
    );
  } else if (infoType === "SUPPORT_MAKING_GENERAL_BOOKING") {
    return (
      <Col span={24} className="filed-help-info">
        <div>
          <div className={SHOW_HELP_INFO_STEPS ? "border-light-grey ff-inter" : "d-none"}>
            <div className="p-20 d-flex info-section align-items-center">
              <Image
                src="/assets/fmlm/info-circle.svg"
                alt=""
                height={25}
                width={25}
                className="mr-10"
              />
              <span className="fs-14 fw-500 ff-inter">
                {intl.formatMessage({ id: "HELP_INFO" })}
              </span>
            </div>
            <div className="p-20 ff-inter">
              <div className="mb-10 fs-14 fw-400 ff-inter color-757575">
                {intl.formatMessage({ id: "bookingSteps" })}:
              </div>
              <div className="p-5">
                <ul>
                  <li className="mb-5 fs-14 fw-400 color-757575">
                    1. {intl.formatMessage({ id: "FromHomeScreen" })}
                  </li>
                  <li className="mb-5 fs-14 fw-400 color-757575">
                    2. {intl.formatMessage({ id: "SelectOriginDestination" })}
                  </li>
                  <li className="mb-5 fs-14 fw-400 color-757575">
                    3. {intl.formatMessage({ id: "SelectNumberOfPass" })}
                  </li>
                  <li className="mb-5">
                    <span className="mr-5 color-757575 fs-14 fw-400">
                      4. {intl.formatMessage({ id: "ClickOn" })}
                    </span>
                    <u {...getLinkProps(homeSearchPath)}>
                      {intl.formatMessage({ id: "search" })}
                    </u>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Col>
    );
  } else if (infoType === "GROUP_BOOKING_9_PASSENGERS") {
    return (
      <Col span={24} className="filed-help-info">
        <div>
          <div className={SHOW_HELP_INFO_STEPS ? "border-light-grey ff-inter" : "d-none"}>
            <div className="p-20 d-flex info-section align-items-center">
              <Image
                src="/assets/fmlm/info-circle.svg"
                alt=""
                height={25}
                width={25}
                className="mr-10"
              />
              <span className="fs-14 fw-500 ff-inter">
                {intl.formatMessage({ id: "HELP_INFO" })}
              </span>
            </div>
            <div className="p-20 ff-inter">
              <div className="mb-10 fs-14 fw-400 ff-inter color-757575">
                {intl.formatMessage({ id: "bookingSteps" })}:
              </div>
              <div className="p-5">
                <ul>
                  <li className="mb-5 fs-14 fw-400 color-757575">
                    1. {intl.formatMessage({ id: "FromHomeScreen" })}
                  </li>
                  <li className="mb-5 fs-14 fw-400 color-757575">
                    2. {intl.formatMessage({ id: "ClickOnPassenger" })}
                  </li>
                  <li className="mb-5 fs-14 fw-400 color-757575">
                    3. {intl.formatMessage({ id: "SelectNumberOfPass" })}
                  </li>
                  <li className="mb-5">
                    <span className="mr-5 color-757575 fs-14 fw-400">
                      4. {intl.formatMessage({ id: "ClickOn" })}
                    </span>
                    <u {...getLinkProps("/group-booking")}>
                      {intl.formatMessage({ id: "Book9Passenger" })}
                    </u>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Col>
    );
  } else if (infoType === "CHANGE_DATE_OR_TIME_CHANGE_OUTBOUND_OR_INBOUND") {
    return (
      <Col span={24} className="filed-help-info">
        <div>
          <div className={SHOW_HELP_INFO_STEPS ? "border-light-grey ff-inter" : "d-none"}>
            <div className="p-20 d-flex info-section align-items-center">
              <Image
                src="/assets/fmlm/info-circle.svg"
                alt=""
                height={25}
                width={25}
                className="mr-10"
              />
              <span className="fs-14 fw-500 ff-inter">
                {intl.formatMessage({ id: "HELP_INFO" })}
              </span>
            </div>
            <div className="p-20 ff-inter">
              <div className="mb-10 fs-14 fw-400 ff-inter color-757575">
                {intl.formatMessage({ id: "CHANGE_BOOKING" })}:
              </div>
              <div className="p-5">
                <ul>
                  <li className="mb-5 fs-14 fw-400 color-757575">
                    1. {intl.formatMessage({ id: "CLICK_BOOKING" })}
                  </li>
                  <li className="mb-5 fs-14 fw-400 color-757575">
                    2. {intl.formatMessage({ id: "OPEN_DESIRED_BOOKING" })}
                  </li>
                  <li className="mb-5 fs-14 fw-400 color-757575">
                    3. {intl.formatMessage({ id: "MANAGE_BOOKING" })}
                  </li>
                  <li className="mb-5">
                    <span className="mr-5 color-757575 fs-14 fw-400">
                      4. {intl.formatMessage({ id: "select" })}
                    </span>
                    <u {...getLinkProps("/bookings")}>
                      {intl.formatMessage({ id: "changeDateTimeText" })}
                    </u>
                    <span className="mr-5 ml-5 color-757575 fs-14 fw-400">
                      {intl.formatMessage({ id: "onlyOR" })}
                    </span>
                    <u {...getLinkProps("/bookings")}>
                      {intl.formatMessage({ id: "ChangeOutboundInboundText" })}
                    </u>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="mt-10 mb-10">
            <Typography.Text className="fw-700 fs-18 ff-inter">
              {intl.formatMessage({ id: "SUBMIT_REQUEST" })}
            </Typography.Text>
          </div>
        </div>
      </Col>
    );
  } else if (infoType === "MANAGE_SEATING_AND_ADDONS") {
    return (
      <Col span={24} className="filed-help-info">
        <div>
          <div className={SHOW_HELP_INFO_STEPS ? "border-light-grey ff-inter" : "d-none"}>
            <div className="p-20 d-flex info-section align-items-center">
              <Image
                src="/assets/fmlm/info-circle.svg"
                alt=""
                height={25}
                width={25}
                className="mr-10"
              />
              <span className="fs-14 fw-500 ff-inter">
                {intl.formatMessage({ id: "HELP_INFO" })}
              </span>
            </div>
            <div className="p-20 ff-inter">
              <div className="mb-10 fs-14 fw-400 ff-inter color-757575">
                {intl.formatMessage({ id: "CHANGE_BOOKING" })}:
              </div>
              <div className="p-5">
                <ul>
                  <li className="mb-5 fs-14 fw-400 color-757575">
                    1. {intl.formatMessage({ id: "CLICK_BOOKING" })}
                  </li>
                  <li className="mb-5 fs-14 fw-400 color-757575">
                    2. {intl.formatMessage({ id: "OPEN_DESIRED_BOOKING" })}
                  </li>
                  <li className="mb-5 fs-14 fw-400 color-757575">
                    3. {intl.formatMessage({ id: "MANAGE_BOOKING" })}
                  </li>
                  <li className="mb-5">
                    <span className="mr-5 color-757575 fs-14 fw-400">
                      4. {intl.formatMessage({ id: "select" })}
                    </span>
                    <u {...getLinkProps("/bookings")}>
                      {intl.formatMessage({ id: "manageSeatingAddOnsText" })}
                    </u>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="mt-10 mb-10">
            <Typography.Text className="fw-700 fs-18 ff-inter">
              {intl.formatMessage({ id: "SUBMIT_REQUEST" })}
            </Typography.Text>
          </div>
        </div>
      </Col>
    );
  } else if (infoType === "UPGRADE_TO_PREMIUM") {
    return (
      <Col span={24} className="filed-help-info">
        <div>
          <div className={SHOW_HELP_INFO_STEPS ? "border-light-grey ff-inter" : "d-none"}>
            <div className="p-20 d-flex info-section align-items-center">
              <Image
                src="/assets/fmlm/info-circle.svg"
                alt=""
                height={25}
                width={25}
                className="mr-10"
              />
              <span className="fs-14 fw-500 ff-inter">
                {intl.formatMessage({ id: "HELP_INFO" })}
              </span>
            </div>
            <div className="p-20 ff-inter">
              <div className="mb-10 fs-14 fw-400 ff-inter color-757575">
                {intl.formatMessage({ id: "CHANGE_BOOKING" })}:
              </div>
              <div className="p-5">
                <ul>
                  <li className="mb-5 fs-14 fw-400 color-757575">
                    1. {intl.formatMessage({ id: "CLICK_BOOKING" })}
                  </li>
                  <li className="mb-5 fs-14 fw-400 color-757575">
                    2. {intl.formatMessage({ id: "OPEN_DESIRED_BOOKING" })}
                  </li>
                  <li className="mb-5 fs-14 fw-400 color-757575">
                    3. {intl.formatMessage({ id: "MANAGE_BOOKING" })}
                  </li>
                  <li className="mb-5">
                    <span className="mr-5 color-757575 fs-14 fw-400">
                      4. {intl.formatMessage({ id: "select" })}
                    </span>
                    <u {...getLinkProps("/bookings")}>
                      {intl.formatMessage({ id: "UPGRADE_TO_PREMIUM" })}
                    </u>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="mt-10 mb-10">
            <Typography.Text className="fw-700 fs-18 ff-inter">
              {intl.formatMessage({ id: "SUBMIT_REQUEST" })}
            </Typography.Text>
          </div>
        </div>
      </Col>
    );
  } else if (infoType === "CANCEL_TICKET") {
    return (
      <Col span={24} className="filed-help-info">
        <div>
          <div className={SHOW_HELP_INFO_STEPS ? "border-light-grey ff-inter" : "d-none"}>
            <div className="p-20 d-flex info-section align-items-center">
              <Image
                src="/assets/fmlm/info-circle.svg"
                alt=""
                height={25}
                width={25}
                className="mr-10"
              />
              <span className="fs-14 fw-500 ff-inter">
                {intl.formatMessage({ id: "HELP_INFO" })}
              </span>
            </div>
            <div className="p-20 ff-inter">
              <div className="mb-10 fs-14 fw-400 ff-inter color-757575">
                {intl.formatMessage({ id: "CHANGE_BOOKING" })}:
              </div>
              <div className="p-5">
                <ul>
                  <li className="mb-5 fs-14 fw-400 color-757575">
                    1. {intl.formatMessage({ id: "CLICK_BOOKING" })}
                  </li>
                  <li className="mb-5 fs-14 fw-400 color-757575">
                    2. {intl.formatMessage({ id: "OPEN_DESIRED_BOOKING" })}
                  </li>
                  <li className="mb-5 fs-14 fw-400 color-757575">
                    3. {intl.formatMessage({ id: "MANAGE_BOOKING" })}
                  </li>
                  <li className="mb-5">
                    <span className="mr-5 color-757575 fs-14 fw-400">
                      4. {intl.formatMessage({ id: "select" })}
                    </span>
                    <u {...getLinkProps("/bookings")}>
                      {intl.formatMessage({ id: "CANCEL_REFUND_TICKET" })}
                    </u>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="mt-10 mb-10">
            <Typography.Text className="fw-700 fs-18 ff-inter">
              {intl.formatMessage({ id: "SUBMIT_REQUEST" })}
            </Typography.Text>
          </div>
        </div>
      </Col>
    );
  } else <></>;
};

export default HelpInfoSection;
